import { WbMoreOptions } from '@agilelab/plugin-wb-platform';
import {
  FeatureRequestListItem,
  FeatureRequestStatus,
} from '@agilelab/plugin-wb-builder-common';
import {
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { FC, useState } from 'react';
import { FeatureRequestStatusBadge } from './FeatureRequestStatusBadge';

type FeatureRequestActionsColumnProps = {
  request: FeatureRequestListItem;
  onView: (request: FeatureRequestListItem) => void;
  onChangeStatus: (
    request: FeatureRequestListItem,
    newStatus: FeatureRequestStatus,
  ) => void;
  onDelete: (request: FeatureRequestListItem) => void;
};

const useStyles = makeStyles(
  theme => ({
    statusEditElement: {
      minWidth: 200,
      padding: theme.spacing(1),
    },
    statusEditElementSelected: {
      backgroundColor: theme.palette.softened.primary,
    },
  }),
  { name: 'FeatureRequestActionsColumn' },
);

export const FeatureRequestActionsColumn: FC<
  FeatureRequestActionsColumnProps
> = ({ request, onView, onChangeStatus, onDelete }) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const onMenuClose = () => setMenuOpen(false);

  return (
    <WbMoreOptions
      open={menuOpen}
      MenuProps={{
        onClick: e => e.stopPropagation(),
      }}
      onClick={e => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
      onClose={onMenuClose}
    >
      <MenuItem
        onClick={e => {
          e.stopPropagation();
          onView(request);
          onMenuClose();
        }}
      >
        <ListItemIcon>
          <VisibilityIcon fontSize="small" />
        </ListItemIcon>
        View Details
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={e => {
          e.stopPropagation();
          setStatusMenuAnchorEl(e.currentTarget);
        }}
      >
        <ListItemIcon>
          <SwapHorizIcon fontSize="small" />
        </ListItemIcon>
        Change Status
        <ListItemIcon style={{ marginLeft: 20 }}>
          <ChevronRightIcon fontSize="small" />
        </ListItemIcon>
      </MenuItem>
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={() => setStatusMenuAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        getContentAnchorEl={null}
      >
        {Object.values(FeatureRequestStatus).map(statusKey => (
          <MenuItem
            disabled={request.status === statusKey}
            key={statusKey}
            className={
              request.status === statusKey
                ? `${classes.statusEditElement} ${classes.statusEditElementSelected}`
                : classes.statusEditElement
            }
            onClick={
              request.status !== statusKey
                ? () => {
                    onChangeStatus(request, statusKey);
                    setStatusMenuAnchorEl(null);
                    onMenuClose();
                  }
                : undefined
            }
          >
            <FeatureRequestStatusBadge status={statusKey} />
          </MenuItem>
        ))}
      </Menu>
      <Divider />
      <MenuItem
        onClick={e => {
          e.stopPropagation();
          onDelete(request);
          onMenuClose();
        }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        Delete
      </MenuItem>
    </WbMoreOptions>
  );
};
